class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.currentMenuOpen = null;
    this.closeTimeout = null;
    this.previousMenuTimeout = null;
    this.submenuTimeout = null;
    this.promoBar = null;
    this.megaMenuOverlay = null;
    this.controller = null;
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.megaMenuOverlay = document.querySelector('[data-mega-menu-overlay]');
    this.promoBar = document.querySelector('[data-promo-bar-inner]') || null;

    const signal = this.controller.signal;

    // Remove scroll lock when restoring from bfcache (back/forward navigation)
    window.addEventListener(
      'pageshow',
      (e) => {
        if (e.persisted) this.closeMenu();
      },
      { signal }
    );

    // Use mouseover delegation so gaps between nav items don't trigger close/open flicker.
    // mouseover fires on every child the cursor touches (unlike mouseenter which only fires on boundary).
    this.addEventListener(
      'mouseover',
      (e) => {
        this.cancelClose();
        const navItem = e.target.closest('[data-nav-item]');
        if (!navItem) return;
        const button = navItem.querySelector('[data-menu-button]');
        if (!button || !button.dataset.menuButton) {
          // Nav item without a mega menu — close any open menu
          this.scheduleClose();
          return;
        }
        this.openMenu(button.dataset.menuButton, button);
      },
      { signal }
    );

    // Schedule close only when the mouse leaves the entire mega-menu element
    this.addEventListener(
      'mouseleave',
      () => {
        this.scheduleClose();
      },
      { signal }
    );

    // Keep menu open when hovering the dropdown itself
    this.querySelectorAll('[data-mega-menu]').forEach((dropdown) => {
      dropdown.addEventListener(
        'mouseenter',
        () => {
          this.cancelClose();
        },
        { signal }
      );

      dropdown.addEventListener(
        'mouseleave',
        () => {
          this.scheduleClose();
        },
        { signal }
      );
    });

    // Submenu triggers: show child links in second column on hover (with delay)
    this.querySelectorAll('[data-submenu-trigger]').forEach((trigger) => {
      trigger.addEventListener(
        'mouseenter',
        () => {
          const dropdown = trigger.closest('[data-mega-menu]');
          if (!dropdown) return;
          this.scheduleSubmenu(dropdown, trigger.dataset.submenuTrigger);
        },
        { signal }
      );
    });

    // Cancel pending submenu switch when hovering the right-column submenu
    this.querySelectorAll('[data-submenu]').forEach((sub) => {
      sub.addEventListener(
        'mouseenter',
        () => {
          this.cancelSubmenu();
        },
        { signal }
      );
    });

    // Hide submenu when hovering a link without children
    this.querySelectorAll(
      '[data-mega-menu] a:not([data-submenu-trigger])'
    ).forEach((link) => {
      // Only target main column links, not submenu child links
      if (link.closest('[data-submenu]')) return;
      link.addEventListener(
        'mouseenter',
        () => {
          this.cancelSubmenu();
          const dropdown = link.closest('[data-mega-menu]');
          if (dropdown) this.hideAllSubmenus(dropdown);
        },
        { signal }
      );
    });

    if (this.megaMenuOverlay) {
      this.megaMenuOverlay.addEventListener(
        'click',
        () => {
          this.cancelClose();
          this.closeMenu();
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.closeMenu();
    this.controller.abort();
  }

  isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  applyScrollLock() {
    if (this.isSafari()) {
      window.cbScrollLock?.lock();
    } else {
      window.cbScrollLock?.lockScroll();
    }
  }

  applyScrollUnlock() {
    if (this.isSafari()) {
      window.cbScrollLock?.unlock();
    } else {
      window.cbScrollLock?.unlockScroll();
    }
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimeout = setTimeout(() => {
      this.closeMenu();
    }, 200);
  }

  cancelClose() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  openMenu(menuId, button) {
    if (this.currentMenuOpen === menuId) return;

    const menu = this.querySelector(`[data-mega-menu="${menuId}"]`);
    if (!menu) return;

    const isSwitching = this.currentMenuOpen !== null;
    if (!isSwitching) this.applyScrollLock();

    menu.style.top = `${this.getBoundingClientRect().bottom}px`;

    // Clear any pending previous menu cleanup
    if (this.previousMenuTimeout) {
      clearTimeout(this.previousMenuTimeout);
      this.previousMenuTimeout = null;
    }

    if (isSwitching) {
      // Instant switch: hide old menus immediately, show new one without transition
      this.querySelectorAll('[data-mega-menu]').forEach((m) => {
        if (m !== menu) {
          m.classList.add('invisible', 'opacity-0', '-z-[900]');
          m.classList.remove('z-[900]', 'z-[901]');
          this.hideAllSubmenus(m);
        }
      });
      menu.style.transition = 'none';
      menu.classList.remove('invisible', 'opacity-0', '-z-[900]');
      menu.classList.add('z-[900]');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          menu.style.transition = '';
        });
      });
    } else {
      // First open: reveal animation (clip-path top to bottom)
      menu.style.transition = 'none';
      menu.classList.remove('invisible', 'opacity-0', '-z-[900]');
      menu.classList.add('z-[900]');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          menu.style.transition = '';
        });
      });
      const duration = parseInt(this.dataset.fadeDuration, 10) || 800;
      menu.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      });
    }

    this.currentMenuOpen = menuId;

    if (this.promoBar) {
      this.promoBar.classList.add('z-30');
    }

    if (this.megaMenuOverlay) {
      this.megaMenuOverlay.classList.remove('hidden');
    }
  }

  closeMenu() {
    this.applyScrollUnlock();

    if (this.previousMenuTimeout) {
      clearTimeout(this.previousMenuTimeout);
      this.previousMenuTimeout = null;
    }
    this.closeAllMenus();
    this.currentMenuOpen = null;

    if (this.megaMenuOverlay) {
      this.megaMenuOverlay.classList.add('hidden');
    }

    if (this.promoBar) {
      this.promoBar.classList.remove('z-30');
    }
  }

  scheduleSubmenu(dropdown, handle) {
    this.cancelSubmenu();
    this.submenuTimeout = setTimeout(() => {
      this.showSubmenu(dropdown, handle);
      this.submenuTimeout = null;
    }, 100);
  }

  cancelSubmenu() {
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
      this.submenuTimeout = null;
    }
  }

  showSubmenu(dropdown, handle) {
    // Hide all submenus in this dropdown
    dropdown.querySelectorAll('[data-submenu]').forEach((sub) => {
      sub.classList.remove('flex');
      sub.classList.add('hidden');
    });

    // Remove active state from all triggers
    dropdown.querySelectorAll('[data-submenu-trigger]').forEach((t) => {
      t.classList.remove('underline');
    });

    // Show the matching submenu
    const submenu = dropdown.querySelector(`[data-submenu="${handle}"]`);
    if (submenu) {
      submenu.classList.remove('hidden');
      submenu.classList.add('flex');
    }

    // Add active state to trigger
    const trigger = dropdown.querySelector(
      `[data-submenu-trigger="${handle}"]`
    );
    if (trigger) {
      trigger.classList.add('underline');
    }
  }

  hideAllSubmenus(dropdown) {
    dropdown.querySelectorAll('[data-submenu]').forEach((sub) => {
      sub.classList.remove('flex');
      sub.classList.add('hidden');
    });
    dropdown.querySelectorAll('[data-submenu-trigger]').forEach((t) => {
      t.classList.remove('underline');
    });
  }

  closeAllMenus() {
    this.querySelectorAll('[data-mega-menu]').forEach((menu) => {
      menu.classList.add('invisible', 'opacity-0', '-z-[900]');
      menu.classList.remove('z-[900]', 'z-[901]');
      this.hideAllSubmenus(menu);
    });
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
